import './SignDocsList.scss'
import {i18n} from "../../../Utils/i18n/i18n";
import {map} from "lodash";
import React, {useMemo} from "react";
import {MgmntSignDocCard} from "../../SignDoc/MgmntSignDocCard/MgmntSignDocCard";
import LoadingSpinner from "../../LoadingSpinner/LoadingSpinner";
import {Draggable} from "../../DragabbleAndSortable/Draggable";
import {useAllAdminSignDocsQuery, invalidateQueries, queryKeys} from "../../Hooks/useQueryHooks";

/**
 * SignDocsList component - displays a list of SignDocs with optional filtering
 *
 * @param {object} params - Component parameters
 * @param {Array} params.types - Array of SignDoc types to fetch
 * @param {Array} params.categories - Array of SignDoc categories to fetch
 * @param {Function} params.filterSignDocsBySearch - Optional filter function for search
 * @param {boolean} params.draggable - If true, wraps each card in a Draggable component
 * @param {boolean} params.noButton - If true, hides action buttons on cards (used with draggable)
 * @param {Array} params.excludeSignDocIds - Array of SignDoc IDs to exclude from the list
 * @returns {JSX.Element} SignDocsList component
 */
export const SignDocsList = ({types, categories, filterSignDocsBySearch, draggable = false, noButton = false, excludeSignDocIds = [], showArchived = false}) => {
    // Use React Query cached hook with frontend filtering
    const {
        data: signDocs,
        isLoading
    } = useAllAdminSignDocsQuery({
        types,
        categories,
        showArchived,
    });

    // Apply search filter and exclusion if provided
    const filteredSignDocs = useMemo(() => {
        if (!signDocs) return [];

        let filtered = signDocs;

        // Apply search filter if provided
        if (filterSignDocsBySearch) {
            filtered = filterSignDocsBySearch({signDocs: filtered});
        }

        // Exclude SignDocs that are in the excludeSignDocIds array
        if (excludeSignDocIds && excludeSignDocIds.length > 0) {
            filtered = filtered.filter(signDoc => !excludeSignDocIds.includes(signDoc.id));
        }

        return filtered;
    }, [signDocs, filterSignDocsBySearch, excludeSignDocIds]);

    // Callback to invalidate cache after edit/delete
    const handleAfterEditComplete = () => {
        invalidateQueries({queryKeys: queryKeys.allAdminSignDocs(showArchived)});
    };

    /**
     * Determine if empty state is due to search filtering
     * @returns {boolean} True if search is active and filtered out all results
     */
    const isEmptyDueToSearch = () => {
        return filterSignDocsBySearch &&
               signDocs &&
               signDocs.length > 0 &&
               filteredSignDocs.length === 0;
    };

    /**
     * Render appropriate empty state message
     * @returns {JSX.Element} Empty state message component
     */
    const renderEmptyState = () => {
        if (isEmptyDueToSearch()) {
            return (
                <div className="SignDocsList__empty-state SignDocsList__empty-state--search">
                    <div className="SignDocsList__empty-state-title">
                        {i18n.translate('LearningManagementPage.noSearchResults')}
                    </div>
                    <div className="SignDocsList__empty-state-hint">
                        {i18n.translate('LearningManagementPage.noSearchResultsHint')}
                    </div>
                </div>
            );
        }

        return (
            <div className="SignDocsList__empty-state">
                {i18n.translate('LearningManagementPage.noSignDocs')}
            </div>
        );
    };

    return <div className="SignDocsList">
        {isLoading ? <LoadingSpinner/> : <>
            <div className="signDocumentsWrapper">
                {map(filteredSignDocs, (signDoc) => {
                    const cardElement = (
                        <MgmntSignDocCard
                            signDoc={signDoc}
                            onAfterEditComplete={handleAfterEditComplete}
                            noButton={noButton}
                        />
                    );

                    return (
                        <div className="signDocumentWrapper" key={signDoc.id}>
                            {draggable ? (
                                <Draggable id={signDoc.id}>
                                    {cardElement}
                                </Draggable>
                            ) : (
                                cardElement
                            )}
                        </div>
                    );
                })}
                {filteredSignDocs && filteredSignDocs.length === 0 && renderEmptyState()}
            </div>
        </>}

    </div>
}